import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { ReactNode } from "react";
import { get } from "../../lib/api";
import { kwh, timestamp } from "../../lib/format";
import {
  curveCaption,
  displayEnergy,
  explainAnomaly,
  kilowatts,
  outcomeHint,
  sessionDuration,
  sessionStateTone,
  socBarGeometry,
  socSpan,
} from "../../lib/sessions";
import type { SocSpan } from "../../lib/sessions";
import type { SessionDetail } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { Freshness } from "../ui/Freshness";
import { Modal } from "../ui/Modal";
import { Pill, StatusMark } from "../ui/Status";
import { Metric, MicroLabel } from "../ui/Text";
import { PowerCurve } from "./PowerCurve";
import { SessionAudit } from "./SessionAudit";
import { SessionParty } from "./SessionParty";

/**
 * One charging session, over the list it was clicked from.
 *
 * Arranged by how much each fact matters to the operator who opened it, not by
 * which table it came from. Four tiers, each a step quieter than the last:
 *
 * 1. What went wrong, if anything — the anomaly flags, in words, above everything.
 * 2. The answer — energy at display size, then state and how long it ran.
 * 3. The shape — the power curve and the state of charge it bought.
 * 4. The evidence — registers, both clocks, attribution, the raw transaction data.
 *    An auditor needs every one of these and an operator almost never does, so
 *    they sit behind a disclosure whose summary says whether opening it is worth it.
 *
 * Who charged and where sits to the side, the way uptime sits beside a charge
 * point's facts: always in view, never competing with the figure.
 *
 * An open session is polled; a closed one is immutable (bar spec 3.6's rare late
 * StopTransaction, for which closing and reopening is the right cost).
 */

const POLL_MS = 15_000;

export function SessionView({
  sessionId,
  open,
  onOpenChange,
}: {
  readonly sessionId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  // The id clears the moment the panel is closed, but the panel is still on screen
  // while it animates out. Holding the last id keeps the session it was showing
  // in place for that exit instead of flashing "Loading".
  const [shownId, setShownId] = useState(sessionId);
  if (sessionId !== "" && sessionId !== shownId) setShownId(sessionId);

  const session = useQuery({
    queryKey: ["session", shownId],
    queryFn: () => get<SessionDetail>(`/sessions/${encodeURIComponent(shownId)}`),
    enabled: open && shownId !== "",
    refetchInterval: (query) => (query.state.data?.state === "active" ? POLL_MS : false),
  });
  const row = session.data;

  return (
    <Modal
      size="wide"
      open={open}
      onOpenChange={onOpenChange}
      eyebrow={row === undefined ? "Session" : `Session · ${row.depotName}`}
      title={row === undefined ? "Session" : `${row.chargePointId} · EVSE ${row.evseId}`}
      footer={
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          Close
        </Button>
      }
    >
      {session.isError ? (
        <ErrorNotice error={session.error} onRetry={() => void session.refetch()} />
      ) : row === undefined ? (
        <span role="status">
          <MicroLabel>Loading</MicroLabel>
        </span>
      ) : (
        <div className="grid grid-cols-1 gap-x-8 gap-y-8 lg:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-8 lg:col-span-2">
            <Anomalies flags={row.anomalyFlags} />
            <Outcome
              row={row}
              freshness={
                row.state === "active" ? (
                  <Freshness
                    updatedAt={session.dataUpdatedAt}
                    isFetching={session.isFetching}
                    intervalMs={POLL_MS}
                  />
                ) : null
              }
            />
            <Shape row={row} />
            <TimelineStrip row={row} />
            <SessionAudit row={row} />
          </div>
          <SessionParty row={row} />
        </div>
      )}
    </Modal>
  );
}

function Anomalies({ flags }: { readonly flags: readonly string[] }) {
  if (flags.length === 0) return null;

  return (
    <section
      aria-label="Worth knowing"
      className="flex flex-col gap-2 border-l-2 border-signal bg-signal-wash/40 px-4 py-3"
    >
      {flags.map((flag) => (
        <p key={flag} className="m-0 flex items-start gap-2 text-body text-ink">
          <span className="pt-1.5">
            <StatusMark tone="faulted" />
          </span>
          {explainAnomaly(flag)}
        </p>
      ))}
    </section>
  );
}

/**
 * Tier 2. Energy is the one figure set large: it is what the session was for and
 * what gets billed. Duration and peak sit beside it a full step down, so the eye
 * lands on the kWh first and reads the rest as its qualification.
 */
function Outcome({
  row,
  freshness,
}: {
  readonly row: SessionDetail;
  readonly freshness: ReactNode;
}) {
  const energy = displayEnergy(row);

  return (
    <section
      aria-label="Outcome"
      className="flex flex-wrap items-end justify-between gap-x-10 gap-y-5"
    >
      <Metric
        label={energy.isEstimate ? "Delivered so far" : "Energy"}
        // The same tilde the table uses: the distinction has to survive greyscale.
        value={energy.wh === null ? "—" : `${energy.isEstimate ? "~" : ""}${kwh(energy.wh)}`}
        unit="kWh"
        tone={energy.isEstimate ? "muted" : "ink"}
        hint={outcomeHint(row)}
      />
      <div className="flex flex-col items-start gap-2 sm:items-end">
        <Pill tone={sessionStateTone(row.state)}>{row.state}</Pill>
        <span className="font-display text-title font-semibold tabular-nums text-ink">
          {sessionDuration(row, Date.now())}
        </span>
        <span className="text-data tabular-nums text-ink-muted">
          peak {kilowatts(row.peakPowerW)} kW
        </span>
        {freshness}
      </div>
    </section>
  );
}

/** Tier 3: how the energy arrived, and what it did to the battery. */
function Shape({ row }: { readonly row: SessionDetail }) {
  const soc = socSpan(row);

  return (
    <section aria-label="Power" className="flex flex-col gap-4">
      {row.curve === null || row.curve.length === 0 ? null : (
        <PowerCurve
          curve={row.curve}
          avgPowerW={row.avgPowerW}
          isLive={row.curveSource === "live"}
        />
      )}
      <p className="m-0 max-w-prose text-data text-ink-faint">{curveCaption(row)}</p>
      {soc === null ? null : <SocBar span={soc} />}
    </section>
  );
}

/**
 * The state of charge as a filled span on a 0–100 track, so "22 → 91" reads as
 * most of a battery at a glance. A missing end is left undrawn rather than guessed.
 */
function SocBar({ span }: { readonly span: SocSpan }) {
  const bar = socBarGeometry(span);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-4">
        <MicroLabel>State of charge</MicroLabel>
        <span className="text-data tabular-nums text-ink">
          {span.from === null ? "?" : `${span.from}%`} → {span.to === null ? "…" : `${span.to}%`}
        </span>
      </div>
      <div
        aria-hidden="true"
        className="relative h-2 w-full overflow-hidden rounded-hairline bg-paper-sunken"
      >
        <span
          className="absolute inset-y-0 bg-state-charging"
          style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
        />
      </div>
    </div>
  );
}

/**
 * When, as a line rather than a grid of four timestamps. The station's clock is
 * the one shown; the server's sits in the audit trail beside it, where the
 * comparison is the point.
 */
function TimelineStrip({ row }: { readonly row: SessionDetail }) {
  const zone = row.depotTimezone;
  const isOpen = row.state === "active";

  return (
    <section aria-label="Timeline" className="flex flex-col gap-1.5">
      <div className="flex items-center gap-3 text-data tabular-nums text-ink">
        <span>{timestamp(row.startedAtCp, zone)}</span>
        <span
          aria-hidden="true"
          className={`h-0 flex-1 border-t ${isOpen ? "border-dashed border-ink-faint" : "border-ink"}`}
        />
        <span>{isOpen ? "now" : timestamp(row.endedAtCp, zone)}</span>
      </div>
      <div className="flex justify-between gap-4 text-micro normal-case tracking-normal text-ink-faint">
        <span>Started · {zone}</span>
        <span>
          {isOpen ? "still charging" : `Stopped${row.stopReason === null ? "" : `: ${row.stopReason}`}`}
        </span>
      </div>
    </section>
  );
}
